/**
 * TEMPORARY, for 86bcvk5j9 (cost of saving a full 50,000-event playback queue).
 * Remove together with seek-debug.ts once the chunked queue passes.
 *
 * With ?debug=queue in the player URL: fills a test queue of 50,000 events through the real
 * PlaybackEventBuffer and IndexedDbPlaybackEventStore, under its own key prefix
 * (playbackEventsDebug:meta / :chunk:<n>), so the real queue is never touched and nothing
 * is sent to the server. Then it records one event every 10 s, 30 times. The queue is full,
 * so each record also trims the oldest event: the head chunk and the newest chunk are
 * rewritten, the worst normal case. The overlay shows how long each save blocks the main
 * thread (record + building the write + IndexedDB put) and the time until the write is done.
 * At the end it shows the median and max and deletes the test data.
 */
import { PlaybackEventBuffer, PLAYBACK_EVENT_CAPACITY, } from '../playback/playback-event-buffer.js';
import { META_STORE, openPlayerDatabase } from './idb/database.js';
import { idbTransactionDone } from './idb/request.js';
import { IndexedDbPlaybackEventStore } from './indexed-db-playback-event-store.js';
import { debugFlagEnabled, showDebugLine } from './seek-debug.js';
const DEBUG_PREFIX = 'playbackEventsDebug';
const EVENT_COUNT = PLAYBACK_EVENT_CAPACITY;
const SAVE_EVERY_MS = 10_000;
const SAVES = 30;
let started = false;
function testUuid() {
    const hex = Array.from({ length: 32 }, () => Math.floor(Math.random() * 16).toString(16)).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-4${hex.slice(13, 16)}-a${hex.slice(17, 20)}-${hex.slice(20)}`;
}
function testEvent(i) {
    return {
        eventType: i % 2 === 0 ? 'play_start' : 'play_end',
        mediaAssetId: testUuid(),
        playlistId: testUuid(),
        playlistVersion: 7,
        itemPosition: i % 5,
        playedAt: new Date(Date.now() - (EVENT_COUNT - i) * 10_000).toISOString(),
        ...(i % 2 === 1 ? { durationMs: 10_000 } : {}),
    };
}
function wait(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}
/** Resolves when the browser runs its next task, i.e. when the main thread is free again. */
function nextTask() {
    return new Promise((resolve) => {
        const channel = new MessageChannel();
        channel.port1.onmessage = () => {
            channel.port1.close();
            resolve();
        };
        channel.port2.postMessage(null);
    });
}
/** Removes the meta key and the old single debug key (left by the previous version of this test). */
async function deleteKeys(db, keys) {
    const tx = db.transaction(META_STORE, 'readwrite');
    const done = idbTransactionDone(tx);
    for (const key of keys)
        tx.objectStore(META_STORE).delete(key);
    await done;
}
async function run() {
    const db = await openPlayerDatabase();
    // A reload mid-test could leave the last run behind: clear it before starting.
    await deleteKeys(db, [DEBUG_PREFIX]);
    const store = new IndexedDbPlaybackEventStore(() => Promise.resolve(db), DEBUG_PREFIX);
    const buffer = new PlaybackEventBuffer(store);
    await buffer.init();
    buffer.clear();
    await buffer.flushWrites();
    showDebugLine(`queue test: filling ${EVENT_COUNT} test events (chunked store)`);
    for (let i = 0; i < EVENT_COUNT; i += 1)
        buffer.record(testEvent(i));
    await buffer.flushWrites();
    showDebugLine(`queue test: ${buffer.size()} events stored in chunks`);
    const blocked = [];
    for (let n = 1; n <= SAVES; n += 1) {
        await wait(SAVE_EVERY_MS);
        const t0 = performance.now();
        buffer.record(testEvent(EVENT_COUNT + n));
        await nextTask();
        const blockedMs = performance.now() - t0;
        await buffer.flushWrites();
        const totalMs = performance.now() - t0;
        blocked.push(blockedMs);
        const clock = new Date().toISOString().slice(11, 23);
        showDebugLine(`${clock} queue save ${n}/${SAVES}: main thread ${Math.round(blockedMs)} ms, ` +
            `done in ${Math.round(totalMs)} ms`);
    }
    buffer.clear();
    await buffer.flushWrites();
    await deleteKeys(db, [`${DEBUG_PREFIX}:meta`, DEBUG_PREFIX]);
    const sorted = [...blocked].sort((a, b) => a - b);
    const median = sorted[Math.floor(sorted.length / 2)] ?? 0;
    const max = sorted[sorted.length - 1] ?? 0;
    showDebugLine(`queue test done: main thread median ${Math.round(median)} ms, max ${Math.round(max)} ms. Test data removed`);
}
/** Starts the queue save test once, only with ?debug=queue. */
export function startQueueSaveDebugOnce() {
    if (started || !debugFlagEnabled('queue'))
        return;
    started = true;
    void run().catch((error) => {
        showDebugLine(`queue test failed: ${error instanceof Error ? error.message : String(error)}`);
    });
}
//# sourceMappingURL=queue-debug.js.map